// @flow
import * as R from 'ramda';

import { type FieldProps, getIn } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';

import { APP_CONFIG } from 'constants/index';
import { Colors } from 'assets';
import FormControl from 'react-bootstrap/FormControl';
import FormGroup from 'react-bootstrap/FormGroup';
import FormLabel from 'react-bootstrap/FormLabel';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import MaskedInput, { conformToMask } from 'react-text-mask';
import { UserRedux } from 'state/reducers';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import styled from 'styled-components';
import { unmaskCurrencyToNumber, formatNumber } from 'utilities/stringUtils';
import { useSelector } from 'react-redux';

const StyledFormGroup = styled(FormGroup)`
  padding: 0px !important;
  width: 250px;
  .is-invalid {
    &::placeholder {
      color: ${Colors.redDanger} !important;
    }
 };
  .remainingBalance {
    font-size: small;
    margin-top: 5px;
    text-align: right;
  };
  .btn-link {
    font-size: small;
    padding: 4px;
  }
`;

// mask for currency input
// thousand separator is default
const currencyMask = createNumberMask({
    // decimalLimit: 4,
  prefix: '',
  suffix: '',
  allowDecimal: true, // allow the user to enter a fraction with the amount
  integerLimit: 26,
 // requireDecimal: true, // always include a decimal point and placeholder for decimal digits after the integer
});

type Props = {
  className: string,
  label: string,
  remainingBalance?: number,
};

const PaymentField = ({
  field,
  form: { touched, errors, setFieldValue, setFieldTouched },
  label,
  className,
  remainingBalance,
  ...restProps
}: FieldProps<any> & Props) => {
  const currency: string = useSelector(
    R.pipe(UserRedux.getReducerState, UserRedux.selectors.getCurrencySymbol)
  );

  const _error = getIn(errors, field.name);
  const _touched = getIn(touched, field.name);

  // Used for formatting and internal state tracking of user input.
  const [localValue, setLocalValue] = useState(formatNumber(field.value));
  // Formatted value representing the remaining balance after subtracting the input amount.
  // Displayed in the "Remaining Balance" link as a suggested fill-in value.
  const [adjustedRemainingFormatted, setAdjustedRemainingFormatted] = useState('');

  const getAdjustedRemaining = (value: string | number) => {
    const inputValue = Number(unmaskCurrencyToNumber(String(value || '0')));
    const diff = (remainingBalance || 0) - inputValue;
    const valueDiff = Math.max(diff, 0);
    const formatted = conformToMask(formatNumber(valueDiff), currencyMask, { guide: false }).conformedValue;
    setAdjustedRemainingFormatted(formatted);
  };

  useEffect(() => {
    setLocalValue(formatNumber(field.value));
    getAdjustedRemaining(field.value);
  }, [field.value, remainingBalance]);

  const handleInputChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setLocalValue(value);
    getAdjustedRemaining(value);
  };

  const handleInputBlur = () => {
    const unmasked = unmaskCurrencyToNumber(localValue);
    setFieldValue(field.name, unmasked);
    setFieldTouched(field.name, true);
    setLocalValue(formatNumber(unmasked)); 
  };

  const onRemainingBalanceClick = () => {
    const originalRemaining = Number(remainingBalance || 0);

    if(localValue < remainingBalance){
      const formattedValue = conformToMask(formatNumber(originalRemaining), currencyMask, { guide: false }).conformedValue;

      setLocalValue(formattedValue);
      setFieldValue(field.name, originalRemaining);
      setFieldTouched(field.name, true);
    }
  };

  return (
    <StyledFormGroup className={`m-0 py-2 ${className}`}>
      {
      label && <FormLabel>{label}</FormLabel>
      }
      <InputGroup>
        <InputGroup.Prepend>
          <InputGroup.Text>{currency || APP_CONFIG.defaultCurrency.symbol}</InputGroup.Text>
        </InputGroup.Prepend>
        <FormControl
          as={MaskedInput}
          mask={currencyMask}
          {...restProps}
          isInvalid={_error && _touched}
          value={localValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
        />
        <FormControl.Feedback type="invalid">
          {_error}
        </FormControl.Feedback>
      </InputGroup>

      {remainingBalance > 0 && (
        <div className="remainingBalance">
          Remaining balance:{' '}
          <Button variant="link" onClick={onRemainingBalanceClick}>
            {currency || APP_CONFIG.defaultCurrency.symbol}
            {adjustedRemainingFormatted}
          </Button>
        </div>
      )}
    </StyledFormGroup>
  );
};

export default PaymentField;
